import { useMemo } from 'react';
import { getYesNoOptions, Badge, RadioGroup, TextInput, YesNoAnswer } from '../../../../components';
import { PROSPECT_CATEGORY_TO_COLOR, PROSPECT_TYPE_TO_LABEL } from '../../../../constants';
import { ComponentSize, LD_FeatureFlags, Permissions, ProspectScreenStyle } from '../../../../types';
import { useAppDispatch, useAppSelector, useFeatureFlag } from '../../../../hooks';
import { setIsHidden, setIsRestricted, setScreenStyle } from '../../../../redux/reducers';
import { conditionalArray, snakeCaseToLabel } from '../../../../utils';
import ProspectFieldGroup from '../ProspectFieldGroup';
import TabSection from '../TabSection';

const PROSPECT_SCREEN_STYLE_OPTIONS = Object.values(ProspectScreenStyle).map((value) => ({
  label: snakeCaseToLabel(value),
  value,
}));

interface CallTypeProps {
  isSuperAdmin: boolean;
}
const CallType = ({ isSuperAdmin }: CallTypeProps) => {
  const dispatch = useAppDispatch();
  const {
    category,
    type,
    fields: { isHidden, isRestricted, screenStyle },
  } = useAppSelector((state) => state.prospect);

  const permissions = useAppSelector((state) => state.auth.user?.permissions) || [];
  const canManageProspect = permissions.includes(Permissions.MANAGE_PROSPECT);

  const fullScreenCallsFF = useFeatureFlag(LD_FeatureFlags.RELEASE_FULL_SCREEN_CALLS);

  const allFields = useMemo(
    () => [
      // Show the simulation category and type selected at the very beginning of prospect creation.
      {
        label: 'Simulation category',
        content: (
          <Badge
            size={ComponentSize.MEDIUM}
            className="h-8 rounded-lg"
            color={category ? PROSPECT_CATEGORY_TO_COLOR[category] : undefined}
            label={category ? snakeCaseToLabel(category) : undefined}
          />
        ),
      },
      {
        label: 'Scenario type',
        content: <TextInput disabled value={type ? PROSPECT_TYPE_TO_LABEL[type] : undefined} />,
      },
      ...conditionalArray(fullScreenCallsFF, {
        label: 'Screen style',
        content: (
          <RadioGroup
            options={PROSPECT_SCREEN_STYLE_OPTIONS}
            value={screenStyle}
            onChange={(e) => dispatch(setScreenStyle(e.target.value as ProspectScreenStyle))}
          />
        ),
      }),
      ...conditionalArray(canManageProspect, {
        // We store internally as "isHidden" but display as "Public?".
        // So we invert the yes/no value when setting isHidden.
        label: 'Public?',
        tooltip: 'Non-public prospects are hidden from representatives.',
        content: (
          <RadioGroup
            options={getYesNoOptions()}
            value={isHidden ? YesNoAnswer.NO : YesNoAnswer.YES}
            onChange={(e) => dispatch(setIsHidden(e.target.value === YesNoAnswer.NO))}
          />
        ),
      }),
      ...conditionalArray(isSuperAdmin, {
        // We store internally as "isRestricted" and display as "Is restricted?".
        // So yes/no maps directly to the boolean.
        label: 'Is restricted?',
        content: (
          <RadioGroup
            options={getYesNoOptions()}
            value={isRestricted ? YesNoAnswer.YES : YesNoAnswer.NO}
            onChange={(e) => dispatch(setIsRestricted(e.target.value === YesNoAnswer.YES))}
          />
        ),
      }),
    ],
    [category, canManageProspect, fullScreenCallsFF, isHidden, screenStyle, type, isSuperAdmin, isRestricted]
  );

  // Split fields into two rows if the number of fields is greater than 4.
  const shouldSplitFields = allFields.length > 4;
  const firstRowFields = shouldSplitFields ? allFields.slice(0, 4) : allFields;
  const secondRowFields = shouldSplitFields ? allFields.slice(4) : [];

  return (
    <TabSection title="Call type">
      <ProspectFieldGroup fields={firstRowFields} spaced />
      {secondRowFields.length > 0 && <ProspectFieldGroup fields={secondRowFields} spaced />}
    </TabSection>
  );
};

export default CallType;
